// src/components/modals/ForcePunchModal.jsx
// Modal shown when a POS order fails due to insufficient stock.
// Allows users with FORCE_PUNCH_ORDER permission to override and place the order anyway.

import React, { useState } from 'react'
import { AlertTriangle, X, Loader2, Zap } from 'lucide-react'

/**
 * ForcePunchModal
 *
 * Props:
 *   isOpen        {boolean}  — controls visibility
 *   onClose       {function} — called when user cancels
 *   stockMessage  {string}   — API error message listing unavailable items
 *   onForcePunch  {function} — async function; called when user confirms force-punch
 *                              Should return { success, message } or throw on hard failure.
 *   canForcePunch {boolean}  — whether the current user's role allows force-punch
 */
const ForcePunchModal = ({
  isOpen,
  onClose,
  stockMessage = '',
  onForcePunch,
  canForcePunch = false,
}) => {
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  if (!isOpen) return null

  const handleForcePunch = async () => {
    if (!onForcePunch) return
    setLoading(true)
    setErrorMsg('')
    try {
      await onForcePunch()
      // Parent is responsible for closing modal on success
    } catch (err) {
      setErrorMsg(err?.message || 'Force punch failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (loading) return // prevent closing while processing
    setErrorMsg('')
    onClose?.()
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose() }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-red-500 to-orange-500 text-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-bold leading-tight">
              Out of Stock — Force Punch?
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-1.5 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pt-5 pb-2 space-y-4">
          {/* Stock shortage details from API */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-red-700 mb-1">Stock Shortage Detected</p>
            <p className="text-sm text-red-600 leading-relaxed whitespace-pre-line">
              {stockMessage || 'One or more items in this order are out of stock.'}
            </p>
          </div>

          {/* Warning text */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700 leading-relaxed">
              Stock is currently unavailable. Proceeding will record a stock deficit and notify management.
            </p>
          </div>

          {/* Force punch not allowed message */}
          {!canForcePunch && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <p className="text-sm text-gray-600">
                Your role does not have permission to force-punch orders. Please contact a manager or admin.
              </p>
            </div>
          )}

          {/* Error from force-punch attempt */}
          {errorMsg && (
            <div className="bg-red-50 border border-red-300 rounded-xl p-3">
              <p className="text-sm text-red-700 font-medium">{errorMsg}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-5 flex gap-3">
          {/* Cancel */}
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex-1 px-4 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm
                       hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>

          {/* Force Punch — only shown if user has permission */}
          {canForcePunch && (
            <button
              onClick={handleForcePunch}
              disabled={loading}
              className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-red-500 to-rose-600 text-white
                         font-bold text-sm flex items-center justify-center gap-2 shadow-md
                         hover:from-red-600 hover:to-rose-700 transition-all
                         disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Processing…</span>
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  <span>Force Punch</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ForcePunchModal
